// src/com/veh_info/VehInfoInputRental.js
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import { AppWord, AppObject, AppStyle, CodeGroup, InputType } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import { VzmInputBlock, ErrorHintBox } from '../../common/VzmStyled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { VehInfoDictionary } from './VehInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import PhoneField from '../../common/PhoneField';
import { useSnackbar } from 'notistack';
import { GET_VEH_INFO_BY_PLATES } from './VehInfoGql';
import {
    codeTreeRepo, comInfoListRepo, userInfoRepo
} from '../../value/AppModel';
import VzmSelector from '../../common/VzmSelector';
import ValueMisc from '../../value/ValueMisc';

const InputMap = VehInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (VEH_INFO) 정보 생성 또는 편집 Dialog
 * Vehicle Info
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-04-21 15:50:25 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function VehInfoInputRental({
    open,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const codeTree = useReactiveVar(codeTreeRepo);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const comInfoList = useMemo(()=>{
        if(comInfoListData.records.length > 0) {
            //const list = comInfoListData.records.filter(com=>com.dcCd===sessionInfo.user.dscIdAs) //[...comInfoListData.records];
            
            const targetId = sessionInfo.user?.dscIdAs ? String(sessionInfo.user.dscIdAs) : '';
            const isTenDigits = targetId.length === 10;

            const list = comInfoListData.records.filter((com) => {
                if (isTenDigits) {
                    // 길이가 10인 경우 com.comId와 비교
                    return String(com.comId) === targetId;
                } else {
                    // 길이가 10이 아닌 경우 원래대로 com.dcCd와 비교
                    return String(com.dcCd) === targetId;
                }
            });


            list.unshift({comId:AppWord.ChooseValue, comName:AppWord.ChooseKorean});
            return list;
        }
        return [{comId:AppWord.ChooseValue, comName:AppWord.ChooseKorean}];
    }, [comInfoListData, sessionInfo]);
    const [transVehTypeOptions, setTransVehTypeOptions] = useState([]);
    const [transVehTonOptions, setTransVehTonOptions] = useState([]);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [transVehTypeCd, setTransVehTypeCd] = useState(AppWord.ChooseValue);
    const [transVehTonCd, setTransVehTonCd] = useState(AppWord.ChooseValue);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.
    const [getVehInfoItemByPlates, responseItem] = useLazyQuery(GET_VEH_INFO_BY_PLATES, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetItem(data, option)},
        onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Get, message: [error.message]})
    });

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);
    // 차종: TRANS_VEH_TYPE_CD

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item]);

    useEffect(()=>{
        const typeOptions = ValueMisc.getCodes(codeTree, CodeGroup.TRANS_VEH_TYPE_CD);
        typeOptions.unshift({codeId:AppWord.ChooseValue, codeName:AppWord.ChooseKorean});
        setTransVehTypeOptions(typeOptions);

        const tonOptions = ValueMisc.getCodes(codeTree, CodeGroup.TRANS_VEH_TON_CD);
        tonOptions.unshift({codeId:AppWord.ChooseValue, codeName:AppWord.ChooseKorean});
        setTransVehTonOptions(tonOptions);
    }, [codeTree]);

    useEffect(()=>{
        // setTransVehTypeCd 호출을 위해서.
        if(open && input.data.transVehTypeCd && transVehTypeOptions.length > 0) {
            const type = transVehTypeOptions.find(c=>c.codeName===input.data.vehTypeName);
            if(type) setTransVehTypeCd(type.codeId);
        }
    }, [open, input, transVehTypeOptions]);

    useEffect(()=>{
        // setTransVehTonCd 호출을 위해서.
        if(open && input.data.transVehTonCd && transVehTonOptions.length > 0) {
            const ton = transVehTonOptions.find(c=>c.codeName===input.data.vehTonName);
            if(ton) setTransVehTonCd(ton.codeId);
        }
    }, [open, input, transVehTonOptions]);

    const onCompleteGetItem = (data, option) => {
        let duplicate = false;

        if(data.vehInfoItem) {
            if(isEdit) {
                if(data.vehInfoItem.vehInfoSeq !== item.vehInfoSeq) {
                    duplicate = true;
                }
            }
            else duplicate = true;
        }

        if(duplicate) {
            addAlert({
                title: '차량번호 중복 확인',
                message: ['이미 등록된 차량입니다.'],
            });
        }
        else {
            enqueueSnackbar('등록할 수 있는 차량번호입니다.', { variant: AppStyle.mui.success, autoHideDuration: 1500 });
        }
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };
    
    const onChangeSelect = (id, value) => {
        onChangeValue(id, value === AppWord.ChooseValue ? undefined : value, false);
    };

    const onChangeTransVehType = (id, value) => {
        setTransVehTypeCd(value);
        const type = transVehTypeOptions.find(c=>c.codeId===value);
        if(type) {
            onChangeValue(InputMap.vehTypeName.id, type.codeName, false);
        }
    };

    const onChangeTransVehTon = (id, value) => {
        setTransVehTonCd(value);
        const ton = transVehTonOptions.find(c=>c.codeId===value);
        if(ton) {
            onChangeValue(InputMap.vehTonName.id, ton.codeName, false);
        }
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const onClickCheckDuplicate = () => {
        if(input.data.vehPlates) {
            getVehInfoItemByPlates({variables:{comId: input.data.comId, vehPlates:input.data.vehPlates}});
        }
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClickCancel}
            title={isEdit ? '용차 정보 수정' : '용차 정보 등록'}
        >
            <DialogContent>
                <Box style={{padding: 10, fontWeight: 'bold', fontSize: '1.05rem'}}>
                    {sessionInfo.user.dscName}
                </Box>
                <VzmInputBlock>
                    <VzmSelector
                        dict={InputMap.comId}
                        value={input.data.comId || AppWord.ChooseValue}
                        onChange={onChangeSelect}
                        list={comInfoList}
                        fullWidth={true}
                        variant={AppStyle.mui.standard}
                        disabled={isEdit}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.vehPlates}
                        value={input.data.vehPlates}
                        onChange={onChangeValue}
                    />
                    <Button
                        variant={AppStyle.mui.outlined}
                        color={AppStyle.mui.primary}
                        onClick={onClickCheckDuplicate}
                        style={{marginLeft: 5}}
                        disabled={!input.data.vehPlates || !input.data.comId || !input.changed}
                    >중복확인</Button>
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmSelector
                        dict={{
                                id: 'codeId', label: '차종',
                                type: InputType.select,
                                required: true, toAdd: true, toEdit: true,
                                labelKey: 'codeName',
                            }}
                        list={transVehTypeOptions}
                        fullWidth={true}
                        value={transVehTypeCd || AppWord.ChooseValue}
                        onChange={onChangeTransVehType}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmSelector
                        dict={{
                                id: 'codeId', label: '톤수',
                                type: InputType.select,
                                required: true, toAdd: true, toEdit: true,
                                labelKey: 'codeName',
                            }}
                        list={transVehTonOptions}
                        fullWidth={true}
                        value={transVehTonCd || AppWord.ChooseValue}
                        onChange={onChangeTransVehTon}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.driverName}
                        value={input.data.driverName}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <PhoneField
                        dict={InputMap.driverMobile}
                        value={input.data.driverMobile}
                        onChange={onChangeValue}
                        isMobile={true}
                    />
                </VzmInputBlock>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed || responseItem.loading}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
